// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║   🔮 DONATAPP - SFERA VOCALE ANIMATA (Stile ChatGPT/Google)              ║
// ║   File: src/components/VoiceOrb.jsx                                      ║
// ║                                                                           ║
// ║   Animazione sfera/cerchio che reagisce alla voce                        ║
// ║   Stati: IDLE, LISTENING, THINKING, SPEAKING                             ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

import React, { useState, useEffect, useRef } from 'react';

// 🎨 Colori per ogni stato (soft, non viola)
const stateColors = {
  idle: {
    primary: '#90CAF9',      // Blu chiaro soft
    secondary: '#E3F2FD',    // Blu ultra chiaro
    accent: '#1976D2',       // Blu accent
  },
  listening: {
    primary: '#81C784',      // Verde soft
    secondary: '#E8F5E9',    // Verde chiaro
    accent: '#4CAF50',       // Verde accent
  },
  thinking: {
    primary: '#FFE082',      // Giallo soft
    secondary: '#FFF8E1',    // Giallo chiaro
    accent: '#FF9800',       // Arancione accent
  },
  speaking: {
    primary: '#F48FB1',      // Rosa soft
    secondary: '#FCE4EC',    // Rosa chiaro
    accent: '#E91E63',       // Rosa accent
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// 🔮 COMPONENTE SFERA PRINCIPALE
// ═══════════════════════════════════════════════════════════════════════════

const VoiceOrb = ({ 
  state = 'idle',           // 'idle' | 'listening' | 'thinking' | 'speaking'
  size = 200,               // Dimensione sfera
  message = '',             // Messaggio da mostrare
  showLabel = true,         // Mostra etichetta stato
  voiceName = 'Assistente', // Nome voce attiva
  audioLevel = 0,           // 0-1, livello audio per reattività
}) => {
  const colors = stateColors[state] || stateColors.idle;
  const [particles, setParticles] = useState([]);
  
  // Genera particelle fluttuanti
  useEffect(() => {
    const newParticles = Array.from({ length: 8 }, (_, i) => ({
      id: i,
      angle: (i * 45) * (Math.PI / 180),
      distance: 60 + Math.random() * 20,
      size: 4 + Math.random() * 4,
      speed: 2 + Math.random() * 2,
    }));
    setParticles(newParticles);
  }, []);
  
  // Velocità animazione per stato
  const animationDuration = {
    idle: '4s',
    listening: '2s',
    thinking: '1s',
    speaking: '0.8s',
  };
  
  // Scala basata su audio level
  const audioScale = 1 + (audioLevel * 0.2);
  
  // Etichette stato
  const stateLabels = {
    idle: '✨ Sono qui',
    listening: '👂 Ti ascolto...',
    thinking: '💭 Penso...',
    speaking: `🗣️ ${voiceName}`,
  };
  
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 40,
    }}>
      
      {/* CSS Animations */}
      <style>{`
        @keyframes orb-breathe {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        
        @keyframes orb-pulse {
          0%, 100% { transform: scale(1); opacity: 0.8; }
          50% { transform: scale(1.1); opacity: 1; }
        }
        
        @keyframes orb-thinking {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes ring-expand {
          0% { transform: scale(0.8); opacity: 0.6; }
          100% { transform: scale(1.5); opacity: 0; }
        }
        
        @keyframes particle-orbit {
          0% { transform: rotate(0deg) translateX(var(--distance)); }
          100% { transform: rotate(360deg) translateX(var(--distance)); }
        }
        
        @keyframes glow-pulse {
          0%, 100% { filter: blur(20px); opacity: 0.5; }
          50% { filter: blur(30px); opacity: 0.8; }
        }
        
        @keyframes wave-bar {
          0%, 100% { transform: scaleY(0.3); }
          50% { transform: scaleY(1); }
        }
      `}</style>
      
      {/* Container sfera */}
      <div style={{
        position: 'relative',
        width: size,
        height: size,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        
        {/* Glow di sfondo */}
        <div style={{
          position: 'absolute',
          width: size * 0.8,
          height: size * 0.8,
          borderRadius: '50%',
          background: colors.primary,
          animation: `glow-pulse ${animationDuration[state]} ease-in-out infinite`,
        }} />
        
        {/* Anelli che si espandono (listening/speaking) */}
        {(state === 'listening' || state === 'speaking') && (
          <>
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                style={{
                  position: 'absolute',
                  width: size * 0.6,
                  height: size * 0.6,
                  borderRadius: '50%',
                  border: `2px solid ${colors.accent}`,
                  animation: `ring-expand ${state === 'speaking' ? 1 : 1.5}s ease-out infinite`,
                  animationDelay: `${i * 0.3}s`,
                }}
              />
            ))}
          </>
        )}
        
        {/* Particelle orbitanti (thinking) */}
        {state === 'thinking' && particles.map((p) => (
          <div
            key={p.id}
            style={{
              position: 'absolute',
              width: p.size,
              height: p.size,
              borderRadius: '50%',
              background: colors.accent,
              '--distance': `${p.distance}px`,
              animation: `particle-orbit ${p.speed}s linear infinite`,
              animationDelay: `${p.id * 0.1}s`,
            }}
          />
        ))}
        
        {/* Sfera principale */}
        <div style={{
          width: size * 0.5,
          height: size * 0.5,
          borderRadius: '50%',
          background: `radial-gradient(circle at 30% 30%, ${colors.secondary}, ${colors.primary})`,
          boxShadow: `
            0 0 40px ${colors.primary}80,
            inset 0 0 30px ${colors.secondary}
          `,
          transform: `scale(${audioScale})`,
          transition: 'transform 0.1s ease-out',
          animation: state === 'thinking' 
            ? 'none' 
            : `orb-${state === 'idle' ? 'breathe' : 'pulse'} ${animationDuration[state]} ease-in-out infinite`,
        }}>
          
          {/* Riflesso lucido */}
          <div style={{
            position: 'absolute',
            top: '15%',
            left: '20%',
            width: '30%',
            height: '20%',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.4)',
            transform: 'rotate(-30deg)',
          }} />
        </div>
        
        {/* Onde audio (speaking) */}
        {state === 'speaking' && (
          <div style={{
            position: 'absolute',
            bottom: -10,
            display: 'flex',
            alignItems: 'flex-end',
            gap: 4,
            height: 30,
          }}>
            {[1, 2, 3, 4, 5, 4, 3, 2, 1].map((h, i) => (
              <div
                key={i}
                style={{
                  width: 4,
                  height: 30,
                  background: colors.accent,
                  borderRadius: 2,
                  transformOrigin: 'bottom',
                  animation: `wave-bar 0.5s ease-in-out infinite`,
                  animationDelay: `${i * 0.05}s`,
                }}
              />
            ))}
          </div>
        )}
      </div>
      
      {/* Label stato */}
      {showLabel && (
        <div style={{
          marginTop: 24,
          fontSize: 18,
          fontWeight: 600,
          color: colors.accent,
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}>
          {stateLabels[state]}
        </div>
      )}
      
      {/* Messaggio */}
      {message && (
        <div style={{
          marginTop: 12,
          fontSize: 16,
          color: '#666',
          textAlign: 'center',
          maxWidth: 300,
          fontStyle: 'italic',
        }}>
          "{message}"
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// 🎮 DEMO COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export const VoiceOrbDemo = () => {
  const [currentState, setCurrentState] = useState('idle');
  const [audioLevel, setAudioLevel] = useState(0);
  
  // Simula audio level quando speaking
  useEffect(() => {
    if (currentState === 'speaking') {
      const interval = setInterval(() => {
        setAudioLevel(Math.random());
      }, 100);
      return () => clearInterval(interval);
    } else {
      setAudioLevel(0);
    }
  }, [currentState]);
  
  return (
    <div style={{ 
      padding: 40, 
      background: '#F8F9FA', 
      minHeight: '100vh',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }}>
      <h2 style={{ textAlign: 'center', marginBottom: 20, color: '#1A1A1A' }}>
        🔮 Test Sfera Vocale
      </h2>
      
      <VoiceOrb 
        state={currentState}
        audioLevel={audioLevel}
        message={currentState === 'speaking' ? "Ciao Donata, tutto bene?" : ""}
        voiceName="Marco"
      />
      
      {/* Controlli */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        gap: 12, 
        marginTop: 40,
        flexWrap: 'wrap',
      }}>
        {['idle', 'listening', 'thinking', 'speaking'].map((state) => (
          <button
            key={state}
            onClick={() => setCurrentState(state)}
            style={{
              padding: '12px 24px',
              borderRadius: 12,
              border: 'none',
              background: currentState === state ? '#1976D2' : '#E0E0E0',
              color: currentState === state ? '#fff' : '#333',
              fontWeight: 600,
              cursor: 'pointer',
              textTransform: 'capitalize',
            }}
          >
            {state}
          </button>
        ))}
      </div>
    </div>
  );
};

export default VoiceOrb;
